import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import {
  GetPlayerNewsQueryQuery,
  PositionAbbrev,
  ShortForm,
  ShortFormNewsTypeEnum,
} from "@/graphql/gql/graphql";
import { getShortForms } from "../api";
import { filterEmpty } from "..";

type UsePlayerUpdatesFilters = {
  sport?: string | null;
  newsTypeFilter: ShortFormNewsTypeEnum | null;
  positionFilter: PositionAbbrev | null;
  teamFilter: number | null;
};

const usePlayerUpdates = ({
  sport,
  newsTypeFilter,
  positionFilter,
  teamFilter,
}: UsePlayerUpdatesFilters) => {
  const [selectedSport, setSport] = useState(sport);

  const queryClient = useQueryClient();
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: [
        "player-news",
        sport,
        newsTypeFilter,
        positionFilter,
        teamFilter,
      ],
      enabled: !!selectedSport,
      queryFn: (context) =>
        getShortForms({
          filter: {
            positionAbbrev: positionFilter,
            shortFormNewsType: newsTypeFilter,
            teamNumberFireId: teamFilter,
            cursor: context.pageParam,
            sportName: sport ?? "",
          },
        }),
      getNextPageParam: (lastPage: GetPlayerNewsQueryQuery) =>
        lastPage?.getShortForms?.pageInfo.hasNextPage
          ? lastPage.getShortForms.pageInfo.cursor
          : undefined,
      initialPageParam: undefined,
    });

  const updatesData = useMemo(
    () =>
      data?.pages.reduce((result: ShortForm[], page) => {
        const playerUpdatesList = page.getShortForms?.shortForms || [];
        const tagUpdates = filterEmpty(
          playerUpdatesList.map((response) => response?.entity)
        );
        const list = [...result, ...tagUpdates];
        return filterEmpty(list) as ShortForm[];
      }, []) || [],
    [data]
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["player-updates"] });
  };

  return {
    updatesData,
    isLoading,
    isError,
    getMoreUpdates: fetchNextPage,
    selectedSport,
    setSport,
    invalidate,
    hasNextPage,
    isFetching,
  };
};

export default usePlayerUpdates;
